import { useCurrentUser, useOfflineModeManager } from 'FrontRoyalAngular';
import { streamDashboardPath, type StreamForDashboard } from 'Lessons';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStudentDashboardStreams } from 'Streams';
import { animatePathChange } from 'RouteAnimation';
import './locales/bookmarkedCourses/bookmarkedCourses-en.json';
import { useContentProgress, getStreamProgressStatus } from 'ContentProgress';
import { CourseList, type GetProgressInfo } from 'CourseItemBox';

const BookmarkedAndCompletedCoursesPresentationComponent = ({
    bookmarkedCourses,
    completedCourses,
    onPress,
    getProgressInfo,
}: {
    bookmarkedCourses: StreamForDashboard[];
    completedCourses: StreamForDashboard[];
    onPress: (course: StreamForDashboard) => void;
    getProgressInfo: GetProgressInfo;
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'bookmarkedCourses.bookmarkedCourses' });
    const OfflineModeManager = useOfflineModeManager();

    if (OfflineModeManager.inOfflineMode) return null;

    return (
        <div className="flex flex-col gap-10">
            <CourseList
                courses={bookmarkedCourses}
                header={t('bookmarkedCourses')}
                onPress={onPress}
                getProgressInfo={getProgressInfo}
                initiallyLimitShownCourses
            />
            <CourseList
                courses={completedCourses}
                header={t('completedCourses')}
                onPress={onPress}
                getProgressInfo={getProgressInfo}
                initiallyLimitShownCourses
            />
        </div>
    );
};

export const BookmarkedAndCompletedCoursesPresentation = memo(BookmarkedAndCompletedCoursesPresentationComponent);

export const BookmarkedAndCompletedCourses = memo(() => {
    const { streamProgress } = useContentProgress();
    const currentUser = useCurrentUser();
    const favoriteStreamLocalePackIds = useMemo(
        () => currentUser?.favoriteLessonStreamLocalePacks?.map(lp => lp.id) ?? [],
        [currentUser],
    );
    const completedStreamLocalePackIds = useMemo(
        () => streamProgress.filter(record => !!record.completedAt).map(record => record.localePackId),
        [streamProgress],
    );

    const completedCourses = useStudentDashboardStreams(completedStreamLocalePackIds);
    const allBookmarkedCourses = useStudentDashboardStreams(favoriteStreamLocalePackIds);

    const bookmarkedCourses = useMemo(
        () => allBookmarkedCourses.filter(course => !completedStreamLocalePackIds.includes(course.localePackId)),
        [allBookmarkedCourses, completedStreamLocalePackIds],
    );

    const getProgressInfo = useCallback(
        ({ type, content }: { type: 'stream'; content: StreamForDashboard }) => {
            if (type === 'stream') {
                const progress = streamProgress.find(sp => sp.localePackId === content.localePackId);

                return {
                    status: getStreamProgressStatus(progress),
                };
            }

            return null;
        },
        [streamProgress],
    ) as GetProgressInfo;

    const onPress = useCallback((course: StreamForDashboard) => {
        animatePathChange(streamDashboardPath({ streamTitle: course.title, streamId: course.id }));
    }, []);

    return (
        <BookmarkedAndCompletedCoursesPresentation
            bookmarkedCourses={bookmarkedCourses}
            completedCourses={completedCourses}
            onPress={onPress}
            getProgressInfo={getProgressInfo}
        />
    );
});
